import { cn } from "@/shared/lib/cn";

export interface PulseLoaderProps {
  /**
   * Lo que oye quien usa lector de pantalla. Obligatorio: un punto que
   * late no comunica NADA sin voz, y "Cargando…" genérico tampoco dice
   * qué se está esperando. Por eso cada sitio pasa el suyo
   * ("Restaurando tu sesión…", "Conectando con Google…").
   */
  label: string;
  /** Clases del contenedor — el alto, el fondo, el padding de cada sitio. */
  className?: string;
}

/**
 * El punto que late mientras algo se resuelve.
 *
 * Existía copiado carácter por carácter en dos sitios (el velo de arranque
 * de `AuthProvider` y el callback de Google), y la copia no era solo el
 * `<span>`: eran también las cuatro decisiones que lo hacen accesible
 * —`role="status"`, `aria-live="polite"`, el texto `sr-only` y el
 * `motion-reduce:animate-none`— repetidas a mano. Bastaba tocar una de las
 * dos copias para que una pantalla dejara de anunciarse o siguiera
 * animando para quien pidió menos movimiento, sin que nada se viera roto.
 *
 * `motion-reduce:animate-none` no es cosmético: para algunas personas una
 * animación que no para dispara mareo o migraña, y el sistema operativo ya
 * lo dice con `prefers-reduced-motion`.
 */
export function PulseLoader({ label, className }: PulseLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("flex items-center justify-center", className)}
    >
      <span
        aria-hidden="true"
        className="h-2.5 w-2.5 animate-pulse rounded-full bg-ds-violet-pop motion-reduce:animate-none"
      />
      <span className="sr-only">{label}</span>
    </div>
  );
}
